import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type DownloadStatus = 'downloading' | 'processing' | 'completed' | 'failed';

export interface DownloadTrackerItem {
	/** Unique key used to upsert an item. Local downloads use `local-<ts>-<n>`, server rows use `server-<id>`. */
	trackId: string;
	/** `local` = browser-side download; `server` = a row from the Downloads queue. */
	source: 'local' | 'server';
	/** Downloads row pk for server items (used to re-download a completed file). */
	serverId?: number;
	/** User-facing file name / report title. */
	filename: string;
	status: DownloadStatus;
	/** 0-100 real progress for blob downloads; 0 while a download is indeterminate or a server job is processing. */
	progress: number;
	/** Optional human-readable failure reason. */
	error?: string;
	/** Epoch ms, used for ordering in the dropdown. */
	createdAt: number;
	/** Server items only: has this row ever actually appeared in a poll of
	 * GET /v1/downloads/? A freshly-queued job is tracked locally the instant
	 * it's requested, before the server can possibly have surfaced it yet — so
	 * "absent from this poll" only means "presumed finished / cleaned up" once
	 * we've confirmed the row was real at least once. Until then, absence is
	 * just not-there-yet, not done. */
	observedOnServer?: boolean;
}

interface DownloadsTrackerState {
	items: DownloadTrackerItem[];
	/** Whether the header's download center dropdown is open. Lives here (not
	 * component state) so starting a download from anywhere in the app can pop
	 * it open to show progress. */
	panelOpen: boolean;
}

const MAX_ITEMS = 30;

const initialState: DownloadsTrackerState = { items: [], panelOpen: false };

export const downloadsTrackerSlice = createSlice({
	name: 'downloadsTracker',
	initialState,
	reducers: {
		upsertItem(state, action: PayloadAction<Partial<DownloadTrackerItem> & { trackId: string }>) {
			const idx = state.items.findIndex((item) => item.trackId === action.payload.trackId);
			if (idx >= 0) {
				state.items[idx] = { ...state.items[idx], ...action.payload };
			} else {
				state.items.unshift(action.payload as DownloadTrackerItem);
				if (state.items.length > MAX_ITEMS) {
					state.items = state.items.slice(0, MAX_ITEMS);
				}
			}
		},
		removeItem(state, action: PayloadAction<string>) {
			state.items = state.items.filter((item) => item.trackId !== action.payload);
		},
		clearFinished(state) {
			state.items = state.items.filter((item) => item.status === 'downloading' || item.status === 'processing');
		},
		setPanelOpen(state, action: PayloadAction<boolean>) {
			state.panelOpen = action.payload;
		},
	},
});

export const { upsertItem, removeItem, clearFinished, setPanelOpen } = downloadsTrackerSlice.actions;

export const isActiveDownload = (item: DownloadTrackerItem): boolean =>
	item.status === 'downloading' || item.status === 'processing';

export const selectDownloadItems = (state: { downloadsTracker: DownloadsTrackerState }): DownloadTrackerItem[] =>
	state.downloadsTracker.items;

export const selectActiveDownloadCount = (state: { downloadsTracker: DownloadsTrackerState }): number =>
	state.downloadsTracker.items.filter(isActiveDownload).length;

export const selectPanelOpen = (state: { downloadsTracker: DownloadsTrackerState }): boolean =>
	state.downloadsTracker.panelOpen;

export default downloadsTrackerSlice.reducer;
